import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
 
import {
  View,
  List,
  Text,
  TruncateText,
  IconDashboardLine,
  IconUserLine,
  IconGroupLine,
  IconCalendarMonthLine,
  IconQuizLine,
  IconSettingsLine,
} from '@smarterservices/smarter-ui';

import useAuthStore from '../../core/stores/useAuthStore';
import { SidebarProps, NavigationItem } from '../types';

const Sidebar = ({ collapsed }: SidebarProps) => {
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Define navigation items based on user role
  const getNavItems = (): NavigationItem[] => {
    const role = authStore.user?.role;

    switch (role) {
      case 'admin':
        return [
          { label: 'Dashboard', icon: <IconDashboardLine />, path: '/admin' },
          { label: 'Users', icon: <IconUserLine />, path: '/admin/users' },
          { label: 'Proctors', icon: <IconGroupLine />, path: '/admin/proctors' },
          { label: 'Tests', icon: <IconQuizLine />, path: '/admin/tests' },
          { label: 'Settings', icon: <IconSettingsLine />, path: '/admin/settings' },
        ];
      case 'proctor':
        return [
          { label: 'Dashboard', icon: <IconDashboardLine />, path: '/proctor' },
          { label: 'Sessions', icon: <IconCalendarMonthLine />, path: '/proctor/sessions' },
          { label: 'Students', icon: <IconUserLine />, path: '/proctor/students' },
          { label: 'Settings', icon: <IconSettingsLine />, path: '/proctor/settings' },
        ];
      case 'testTaker':
        return [
          { label: 'Dashboard', icon: <IconDashboardLine />, path: '/test-taker' },
          { label: 'My Tests', icon: <IconQuizLine />, path: '/test-taker/tests' },
          { label: 'Schedule', icon: <IconCalendarMonthLine />, path: '/test-taker/schedule' },
          { label: 'Settings', icon: <IconSettingsLine />, path: '/test-taker/settings' },
        ];
      case 'faculty':
        return [
          { label: 'Dashboard', icon: <IconDashboardLine />, path: '/faculty' },
          { label: 'Students', icon: <IconUserLine />, path: '/faculty/students' },
          { label: 'Tests', icon: <IconQuizLine />, path: '/faculty/tests' },
          { label: 'Schedule', icon: <IconCalendarMonthLine />, path: '/faculty/schedule' },
          { label: 'Settings', icon: <IconSettingsLine />, path: '/faculty/settings' },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  return (
    <View
      as="nav"
      height="100%"
      width={collapsed ? '4rem' : '16rem'}
      background="secondary"
      shadow="resting"
    >
      <View as="div" textAlign="center">
        <Text size="large" weight="bold" color="primary">
          {collapsed ? 'SP' : 'SmarterProctoring'}
        </Text>
      </View>

      <List isUnstyled margin="small 0 0 0">
        {navItems.map((item, index) => (
          <List.Item key={index}>
            <View
              as="div"
              padding="small"
              background={isActive(item.path) ? 'brand' : 'transparent'}
              borderRadius="medium"
              margin="xx-small"
              cursor="pointer"
              onClick={() => navigate(item.path)}
            >
              <View display="flex" className="align-center">
                <View margin={collapsed ? '0 auto' : '0 small 0 0'}>{item.icon}</View>

                {!collapsed && (
                  <TruncateText>
                    <Text color={isActive(item.path) ? 'primary-inverse' : 'primary'}>
                      {item.label}
                    </Text>
                  </TruncateText>
                )}
              </View>
            </View>
          </List.Item>
        ))}
      </List>
    </View>
  );
};

export default Sidebar;
